package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import dev.danko.roblox_craft_ai.shared.model.AssetRevision
import dev.danko.roblox_craft_ai.shared.model.MeshEditRequest
import dev.danko.roblox_craft_ai.shared.model.MeshInfo
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.AssetType
import dev.danko.roblox_craft_ai.shared.model.GenerationStatus
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.uikit.i18n.FriendlyErrors
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLAnchorElement
import kotlin.js.Date

private enum class PanelTab { CODE, FILES, MODEL, ANIMATION, PREP, SKETCH, VERSIONS, REQUEST }

/**
 * Detail view of one work for the side panel (style A): status and the right actions for the situation, then
 * tabs that depend on the work's type. The AI chat opens from the "What should change?" bar under the code.
 */
@Composable
fun AssetPanel(
    asset: AssetResponse,
    showTechnicalDetails: Boolean,
    onSync: (AssetResponse) -> Unit,
    onFullscreenRequest: (AssetResponse) -> Unit,
    onRefactor: (AssetResponse, String) -> Unit,
    onOpenChat: (AssetResponse) -> Unit,
    onRestoreRevision: (AssetResponse, Int) -> Unit,
    onCancel: (AssetResponse) -> Unit,
    onDelete: (AssetResponse) -> Unit,
    onRegenerate: (AssetResponse) -> Unit,
    onEditPrompt: (AssetResponse) -> Unit,
    onReport: (AssetResponse) -> Unit,
    /** Info and tools under the 3D viewer; without it the viewer is shown alone. */
    meshTools: MeshToolsActions? = null
) {
    val s = strings()
    val p = s.panel
    val palette = LocalAppTheme.current.colors
    val ready = asset.status == GenerationStatus.READY
    val working = asset.status == GenerationStatus.PROCESSING || asset.status == GenerationStatus.PENDING
    val isDemo = asset.metadata["demo"] == "true"
    val sketch = asset.metadata["conceptImageUrl"] ?: asset.referenceImages.firstOrNull()?.takeIf { asset.assetType == AssetType.MESH_3D }

    val tabs = buildList {
        if (ready) {
            when (asset.assetType) {
                AssetType.LUAU_SCRIPT -> add(PanelTab.CODE to p.tabCode)
                AssetType.ROJO_PROJECT -> add(PanelTab.FILES to p.tabFiles)
                AssetType.MESH_3D -> {
                    add(PanelTab.MODEL to p.tab3d)
                    if (!asset.luauCode.isNullOrBlank()) add(PanelTab.PREP to p.tabPrep)
                    if (sketch != null) add(PanelTab.SKETCH to p.tabSketch)
                }
                AssetType.ANIMATION -> add(PanelTab.ANIMATION to p.tabAnimation)
            }
            if (asset.assetType == AssetType.LUAU_SCRIPT) add(PanelTab.VERSIONS to "${p.tabVersions} · ${asset.revisions.size.coerceAtLeast(1)}")
        }
        add(PanelTab.REQUEST to p.tabRequest)
    }
    var tab by remember(asset.id) { mutableStateOf(tabs.first().first) }
    if (tabs.none { it.first == tab }) tab = tabs.first().first

    Column(Modifier.fillMaxWidth().gap(16.px)) {
        // Status line.
        Row(Modifier.gap(8.px).flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
            StatusChip(asset.status)
            if (isDemo || asset.assetType == AssetType.ANIMATION || asset.assetType == AssetType.MESH_3D) {
                Badge(if (isDemo) s.common.demoBadge else s.common.betaBadge, palette.statusPending)
            }
            val version = asset.metadata["currentRevision"]?.toIntOrNull() ?: asset.revisions.maxOfOrNull { it.revisionNumber }
            SpanText(
                listOfNotNull(
                    s.assetTypes.name(asset.assetType),
                    version?.takeIf { it > 1 }?.let { p.version(it) },
                    relativeTime(asset.timestamp).takeIf { it.isNotEmpty() }
                ).joinToString(" · "),
                Modifier.fontSize(13.px).color(palette.textSecondary)
            )
        }
        SyncLine(asset)

        // Actions for the situation.
        Row(Modifier.gap(8.px).flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
            val menu = buildList {
                if (ready) add(TileAction(s.common.regenerate, AppIcon.ROTATE) { onRegenerate(asset) })
                add(TileAction(s.assetCard.editPrompt.removePrefix("✏️ "), AppIcon.SPARKLES) { onEditPrompt(asset) })
                if (ready && asset.assetType == AssetType.LUAU_SCRIPT) add(TileAction(s.assetCard.aiChat, AppIcon.MESSAGE) { onOpenChat(asset) })
                if (ready) add(TileAction(s.report.button, AppIcon.FLAG) { onReport(asset) })
                add(TileAction(s.common.delete, AppIcon.TRASH, danger = true) { onDelete(asset) })
            }
            when {
                ready -> {
                    val syncBusy = asset.metadata["uiSyncStatus"] == "PENDING" || asset.metadata["uiSyncStatus"] == "DELIVERED"
                    AppButton(
                        text = s.home.sendToStudio,
                        icon = AppIcon.SEND,
                        loading = syncBusy,
                        enabled = !syncBusy,
                        onClick = { onSync(asset) }
                    )
                    when (asset.assetType) {
                        AssetType.LUAU_SCRIPT -> asset.luauCode?.let { code ->
                            AppButton(text = s.common.copy, icon = AppIcon.COPY, variant = AppButtonVariant.SECONDARY, onClick = {
                                window.navigator.asDynamic().clipboard?.writeText(code)
                                ToastManager.success(s.common.copied)
                            })
                            AppButton(text = p.downloadLua, icon = AppIcon.DOWNLOAD, variant = AppButtonVariant.SECONDARY, onClick = {
                                downloadText("${asset.id}.lua", code)
                            })
                        }
                        AssetType.ROJO_PROJECT -> AppButton(text = s.assetCard.downloadZip, icon = AppIcon.DOWNLOAD, variant = AppButtonVariant.SECONDARY, onClick = {
                            window.open(BackendEndpoint.http("/api/v1/projects/${asset.id}/export-zip"), "_blank")
                        })
                        AssetType.MESH_3D -> asset.payloadUrl?.takeIf { !isDemo }?.let { url ->
                            AppButton(text = p.downloadGlb, icon = AppIcon.DOWNLOAD, variant = AppButtonVariant.SECONDARY, onClick = {
                                window.open(BackendEndpoint.resolveAssetUrl(url), "_blank")
                            })
                        }
                        AssetType.ANIMATION -> Unit
                    }
                }
                working -> AppButton(text = s.common.cancel, variant = AppButtonVariant.SECONDARY, onClick = { onCancel(asset) })
                else -> AppButton(text = s.common.retry, icon = AppIcon.ROTATE, onClick = { onRegenerate(asset) })
            }
            MoreMenu(menu, openUp = false, buttonSize = AppButtonSize.MD, alignStart = true)
        }

        if (working) Progress(asset)
        if (asset.status == GenerationStatus.FAILED) {
            Column(
                Modifier.fillMaxWidth().padding(12.px).borderRadius(12.px).gap(6.px)
                    .backgroundColor(palette.statusFailed.toRgb().copy(alpha = 30)).color(palette.statusFailed)
            ) {
                SpanText(FriendlyErrors.describe(asset.errorMessage, s), Modifier.fontSize(14.px))
                if (showTechnicalDetails && asset.errorMessage != null) {
                    SpanText("${s.errors.details}: ${asset.errorMessage}", Modifier.fontSize(12.px).opacity(0.8))
                }
            }
        }

        // Tabs.
        Row(
            Modifier.fillMaxWidth().gap(4.px).flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap)
                .styleModifier { property("border-bottom", "1px solid ${palette.border}") }
                .attrsModifier { attr("role", "tablist") }
        ) {
            tabs.forEach { (value, label) ->
                val selected = value == tab
                Button(
                    Modifier
                        .height(44.px)
                        .padding(leftRight = 14.px)
                        .border(0.px, LineStyle.None, Colors.Transparent)
                        .styleModifier { property("border-bottom", "2px solid ${if (selected) palette.accent else "transparent"}") }
                        .backgroundColor(Colors.Transparent)
                        .color(if (selected) palette.textPrimary else palette.textSecondary)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.SemiBold)
                        .cursor(Cursor.Pointer)
                        .styleModifier { property("font-family", "inherit") }
                        .onClick { tab = value }
                        .toAttrs {
                            attr("type", "button")
                            attr("role", "tab")
                            attr("aria-selected", selected.toString())
                        }
                ) { Text(label) }
            }
        }

        when (tab) {
            PanelTab.CODE -> CodeTab(asset, onSync, onFullscreenRequest, onRefactor)
            PanelTab.FILES -> FilesTab(asset, onFullscreenRequest)
            PanelTab.MODEL -> {
                if (isDemo) Hint(s.assetCard.demoMeshHint)
                if (meshTools != null && !isDemo) {
                    ModelTab(asset, meshTools)
                } else {
                    MeshViewer(modelUrl = asset.payloadUrl, prompt = asset.prompt, modifier = Modifier.fillMaxWidth())
                }
            }
            PanelTab.ANIMATION -> {
                Hint(s.assetCard.betaAnimationHint)
                MeshViewer(
                    modelUrl = null,
                    prompt = asset.prompt,
                    modifier = Modifier.fillMaxWidth(),
                    assetType = AssetType.ANIMATION,
                    animationKeyframesJson = asset.animationKeyframes ?: asset.metadata["keyframesJson"],
                    animationMetadata = asset.metadata,
                    showTimeline = true,
                    // Room for the figure and the timeline together without scrolling the panel.
                    initialHeightPx = (kotlinx.browser.window.innerHeight * 0.55).toInt().coerceIn(380, 640),
                    initialIsPlaying = false
                )
                meshTools?.let { AnimationRefinePanel(asset, it) }
            }
            PanelTab.PREP -> CodeViewer(code = asset.luauCode.orEmpty(), maxHeight = null, modifier = Modifier.fillMaxWidth())
            PanelTab.SKETCH -> sketch?.let {
                Img(
                    src = BackendEndpoint.resolveAssetUrl(it),
                    attrs = Modifier.fillMaxWidth().borderRadius(12.px).objectFit(ObjectFit.Contain).backgroundColor(palette.background)
                        .toAttrs { attr("alt", p.tabSketch) }
                )
            }
            PanelTab.VERSIONS -> VersionsTab(asset, onRestoreRevision)
            PanelTab.REQUEST -> RequestTab(asset, showTechnicalDetails)
        }
    }
}

/** The 3D viewer with the info/view/tools panel; edits run here and the work updates from the response. */
@Composable
private fun ModelTab(asset: AssetResponse, tools: MeshToolsActions) {
    val t = strings().meshInspector
    val revisionStrings = strings().revisions
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    val view = remember(asset.id) { MeshViewState() }
    val camera = remember(asset.id) { MeshCameraState() }
    var info by remember(asset.id) { mutableStateOf<MeshInfo?>(null) }
    var busy by remember(asset.id) { mutableStateOf(false) }
    var compareWith by remember(asset.id) { mutableStateOf<AssetRevision?>(null) }

    LaunchedEffect(asset.id, asset.payloadUrl) {
        info = null
        info = tools.info(asset.id) ?: MeshInfo()
    }

    fun edit(request: MeshEditRequest) {
        if (busy) return
        busy = true
        scope.launch {
            try {
                val updated = tools.edit(asset.id, request)
                ToastManager.success(t.saved(updated.metadata["currentRevision"]?.toIntOrNull() ?: 1))
            } catch (error: Throwable) {
                ToastManager.error(error.message?.takeIf { it.isNotBlank() } ?: t.failed, title = t.failed)
            } finally {
                busy = false
            }
        }
    }

    fun retexture(request: dev.danko.roblox_craft_ai.shared.model.RetextureRequest) {
        if (busy) return
        busy = true
        ToastManager.info(t.retextureWorking)
        scope.launch {
            try {
                val updated = tools.retexture(asset.id, request)
                ToastManager.success(t.saved(updated.metadata["currentRevision"]?.toIntOrNull() ?: 1))
            } catch (error: Throwable) {
                ToastManager.error(error.message?.takeIf { it.isNotBlank() } ?: t.failed, title = t.failed)
            } finally {
                busy = false
            }
        }
    }

    fun rigPro() {
        if (busy) return
        busy = true
        ToastManager.info(t.rigWorking)
        scope.launch {
            try {
                tools.rigPro(asset.id)
                info = tools.info(asset.id)
                ToastManager.success(t.rigDone)
            } catch (error: Throwable) {
                ToastManager.error(error.message?.takeIf { it.isNotBlank() } ?: t.failed, title = t.failed)
            } finally {
                busy = false
            }
        }
    }

    fun rig() {
        if (busy) return
        busy = true
        ToastManager.info(t.rigWorking)
        scope.launch {
            try {
                tools.rig(asset.id)
                info = tools.info(asset.id)
                ToastManager.success(t.rigDone)
            } catch (error: Throwable) {
                ToastManager.error(error.message?.takeIf { it.isNotBlank() } ?: t.failed, title = t.failed)
            } finally {
                busy = false
            }
        }
    }

    val other = compareWith
    if (other != null) {
        Row(Modifier.fillMaxWidth().gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                "${t.versionLabel(other.revisionNumber)}  ↔  ${t.versionLabel(asset.metadata["currentRevision"]?.toIntOrNull() ?: 1)} (${t.current})",
                Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(palette.textPrimary)
            )
            Spacer()
            AppButton(text = t.closeCompare, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = { compareWith = null })
        }
        Row(Modifier.fillMaxWidth().gap(8.px)) {
            listOf(other.payloadUrl to describeEdit(other.metadata["meshEdit"], t), asset.payloadUrl to t.current).forEach { (url, label) ->
                Column(Modifier.flexGrow(1).flexBasis(0.px).minWidth(0.px).gap(4.px)) {
                    androidx.compose.runtime.key(url) {
                        MeshViewer(
                            modelUrl = url?.let(BackendEndpoint::resolveAssetUrl),
                            prompt = asset.prompt,
                            modifier = Modifier.fillMaxWidth(),
                            viewState = remember { MeshViewState() }.also { it.shading = view.shading; it.lighting = view.lighting; it.background = view.background },
                            cameraState = camera,
                            compact = true,
                            initialHeightPx = 300
                        )
                    }
                    SpanText(label, Modifier.fontSize(12.px).color(palette.textSecondary))
                }
            }
        }
    } else {
        androidx.compose.runtime.key(asset.payloadUrl) {
            MeshViewer(modelUrl = asset.payloadUrl?.let(BackendEndpoint::resolveAssetUrl), prompt = asset.prompt, modifier = Modifier.fillMaxWidth(), viewState = view, cameraState = camera)
        }
    }

    MeshInspector(
        asset = asset,
        view = view,
        info = info,
        busy = busy,
        onEdit = ::edit,
        onRestore = { number ->
            scope.launch {
                try {
                    tools.restore(asset.id, number)
                    ToastManager.success(revisionStrings.restored(number))
                } catch (error: Throwable) {
                    ToastManager.error(error.message ?: t.failed)
                }
            }
        },
        onCompare = { compareWith = it },
        onDownloadGlb = { asset.payloadUrl?.let { window.open(BackendEndpoint.resolveAssetUrl(it), "_blank") } },
        onDownloadObj = { window.open(tools.objZipUrl(asset.id), "_blank") },
        onDownloadSharp = {
            ToastManager.info(t.preparingSharp)
            window.open(tools.sharpGlbUrl(asset.id), "_blank")
        },
        onRetexture = ::retexture,
        retextureCost = tools.retextureCost(),
        onRig = ::rig,
        onRigPro = ::rigPro,
        rigProCost = tools.rigProCost()
    )
}

@Composable
private fun CodeTab(
    asset: AssetResponse,
    onSync: (AssetResponse) -> Unit,
    onFullscreenRequest: (AssetResponse) -> Unit,
    onRefactor: (AssetResponse, String) -> Unit
) {
    val p = strings().panel
    val palette = LocalAppTheme.current.colors
    val errors = asset.metadata["validationErrors"].orEmpty().split("|||").filter { it.isNotBlank() }
    val warnings = asset.metadata["validationWarnings"].orEmpty().split("|||").filter { it.isNotBlank() }
    val validated = asset.metadata["validated"] == "true"
    Row(Modifier.gap(8.px).fontSize(13.px).color(palette.textSecondary), verticalAlignment = Alignment.CenterVertically) {
        Box(Modifier.size(8.px).borderRadius(50.percent).backgroundColor(if (validated && errors.isEmpty()) palette.statusReady else palette.statusPending))
        SpanText(
            (if (validated && errors.isEmpty()) p.validationOk else p.validationIssues(errors.size + warnings.size)) +
                " · " + strings().common.lines(asset.luauCode.orEmpty().lines().size)
        )
    }
    androidx.compose.runtime.key(asset.id, asset.luauCode) {
        CodeEditor(
            initialCode = asset.luauCode.orEmpty(),
            modifier = Modifier.fillMaxWidth(),
            readOnly = false,
            minHeight = 320.px,
            maxHeight = 620.px,
            languageId = "luau",
            assetId = asset.id,
            assetPrompt = asset.prompt,
            onSyncRequest = { onSync(asset) },
            onFullscreenRequest = { onFullscreenRequest(asset) }
        )
    }
    ChangeBar { text -> onRefactor(asset, text) }
}

@Composable
private fun ChangeBar(onSubmit: (String) -> Unit) {
    val p = strings().panel
    val palette = LocalAppTheme.current.colors
    var text by remember { mutableStateOf("") }
    fun submit() {
        val value = text.trim()
        if (value.isEmpty()) return
        onSubmit(value)
        text = ""
    }
    Row(
        Modifier.fillMaxWidth().gap(8.px).padding(topBottom = 8.px, leftRight = 12.px).borderRadius(12.px)
            .border(1.px, LineStyle.Solid, palette.border).backgroundColor(palette.background),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(Modifier.color(palette.textSecondary)) { Icon(AppIcon.MESSAGE) }
        Input(
            type = InputType.Text,
            attrs = Modifier
                .flexGrow(1)
                .minWidth(0.px)
                .height(36.px)
                .border(0.px, LineStyle.None, Colors.Transparent)
                .outline(0.px, LineStyle.None, Colors.Transparent)
                .backgroundColor(Colors.Transparent)
                .color(palette.textPrimary)
                .fontSize(14.px)
                .styleModifier { property("font-family", "inherit") }
                .toAttrs {
                    value(text)
                    attr("placeholder", p.whatToChange)
                    attr("aria-label", p.whatToChange)
                    onInput { text = it.value }
                    onKeyDown { if (it.key == "Enter") submit() }
                }
        )
        AppButton(text = p.change, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, enabled = text.isNotBlank(), onClick = { submit() })
    }
}

@Composable
private fun FilesTab(asset: AssetResponse, onFullscreenRequest: (AssetResponse) -> Unit) {
    var selected by remember(asset.id) { mutableStateOf(asset.files.firstOrNull()) }
    if (asset.files.isEmpty()) return
    FileTreeViewer(files = asset.files, selectedPath = selected?.path, onFileSelected = { selected = it })
    val file = selected ?: asset.files.first()
    CodeViewer(
        code = file.content,
        language = when {
            file.path.endsWith(".json") -> "json"
            file.path.endsWith(".md") -> "markdown"
            else -> "lua"
        },
        maxHeight = null,
        modifier = Modifier.fillMaxWidth(),
        onFullscreenRequest = { onFullscreenRequest(asset) }
    )
}

@Composable
private fun VersionsTab(asset: AssetResponse, onRestoreRevision: (AssetResponse, Int) -> Unit) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    val revisions = asset.revisions.sortedByDescending { it.revisionNumber }
    if (revisions.size <= 1) {
        SpanText(s.panel.noVersions, Modifier.fontSize(14.px).color(palette.textSecondary))
        return
    }
    val current = asset.metadata["currentRevision"]?.toIntOrNull() ?: revisions.first().revisionNumber
    Column(Modifier.fillMaxWidth().gap(8.px)) {
        revisions.forEach { rev ->
            Row(
                Modifier.fillMaxWidth().padding(12.px).gap(12.px).borderRadius(12.px)
                    .border(1.px, LineStyle.Solid, if (rev.revisionNumber == current) palette.accent else palette.border)
                    .backgroundColor(palette.background),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(Modifier.flexGrow(1).minWidth(0.px).gap(4.px)) {
                    Row(Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                        SpanText("v${rev.revisionNumber}", Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).color(palette.textPrimary))
                        if (rev.revisionNumber == current) SpanText(s.panel.currentVersion, Modifier.fontSize(12.px).color(palette.accentText))
                        if (rev.timestamp > 0) SpanText(Date(rev.timestamp.toDouble()).toLocaleString(LocaleController.current.code), Modifier.fontSize(12.px).color(palette.textSecondary))
                    }
                    SpanText(
                        rev.prompt,
                        Modifier.fontSize(13.px).color(palette.textSecondary).overflow(Overflow.Hidden)
                            .styleModifier { property("text-overflow", "ellipsis") }.whiteSpace(WhiteSpace.NoWrap)
                    )
                }
                if (rev.revisionNumber != current) {
                    AppButton(
                        text = s.revisions.restore(rev.revisionNumber),
                        variant = AppButtonVariant.SECONDARY,
                        size = AppButtonSize.SM,
                        onClick = { onRestoreRevision(asset, rev.revisionNumber) }
                    )
                }
            }
        }
    }
}

@Composable
private fun RequestTab(asset: AssetResponse, showTechnicalDetails: Boolean) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    Div(
        Modifier.fillMaxWidth().padding(14.px).borderRadius(12.px).backgroundColor(palette.background)
            .border(1.px, LineStyle.Solid, palette.border).color(palette.textPrimary).fontSize(15.px).lineHeight(1.6)
            .styleModifier { property("white-space", "pre-wrap") }
            .toAttrs()
    ) { Text(asset.prompt) }
    if (asset.referenceImages.isNotEmpty()) {
        SpanText(s.panel.references, Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
        Row(Modifier.gap(8.px).flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap)) {
            asset.referenceImages.forEach { url ->
                Img(
                    src = BackendEndpoint.resolveAssetUrl(url),
                    attrs = Modifier.size(88.px).borderRadius(10.px).objectFit(ObjectFit.Cover)
                        .border(1.px, LineStyle.Solid, palette.borderStrong).toAttrs { attr("alt", "") }
                )
            }
        }
    }
    if (showTechnicalDetails) {
        val keys = listOf("usedProviderId", "provider", "validatorAttempts", "validationErrors", "validationWarnings", "polyCount", "format")
        val rows = keys.mapNotNull { k -> (asset.metadata[k] ?: (if (k == "usedProviderId") asset.usedProviderId else null))?.takeIf { it.isNotBlank() }?.let { k to it } }
        if (rows.isNotEmpty()) {
            SpanText(s.panel.technical, Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
            Column(Modifier.fillMaxWidth().gap(4.px).fontSize(12.px).color(palette.textSecondary)) {
                rows.forEach { (k, v) -> SpanText("$k: ${v.replace("|||", "; ")}") }
                SpanText("id: ${asset.id}")
            }
        }
    }
}

@Composable
private fun Progress(asset: AssetResponse) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    val streamStatus = asset.metadata["streamStatus"]
    val tokens = asset.metadata["streamTokens"]?.toIntOrNull() ?: 0
    val label = when {
        streamStatus == "validating" -> s.assetCard.progressValidating
        streamStatus?.startsWith("retrying") == true -> s.assetCard.progressRetrying
        tokens > 0 -> s.assetCard.progressTokens(tokens)
        else -> s.assetCard.progressProcessing
    }
    val percent = if (tokens > 0) kotlin.math.min(95, 10 + tokens * 85 / 150) else 15
    Column(Modifier.fillMaxWidth().gap(8.px)) {
        SpanText(label.replace(Regex("^[^\\p{L}]+"), ""), Modifier.fontSize(13.px).color(palette.statusProcessing).fontWeight(FontWeight.SemiBold))
        Box(Modifier.fillMaxWidth().height(6.px).borderRadius(999.px).backgroundColor(palette.border).overflow(Overflow.Hidden)) {
            Box(Modifier.width(percent.percent).height(6.px).backgroundColor(palette.accent).styleModifier { property("transition", "width 150ms") })
        }
        asset.luauCode?.takeIf { it.isNotEmpty() }?.let { live ->
            CodeViewer(code = live, showCopyButton = false, maxHeight = 240.px, modifier = Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun SyncLine(asset: AssetResponse) {
    val s = strings().assetCard
    val palette = LocalAppTheme.current.colors
    val (text, color) = when (asset.metadata["uiSyncStatus"]) {
        "PENDING" -> s.syncPending("") to palette.statusPending
        "DELIVERED" -> s.syncDelivering("") to palette.statusProcessing
        "SYNCED" -> s.syncSynced to palette.statusReady
        "FAILED" -> s.syncFailed to palette.statusFailed
        else -> return
    }
    SpanText(text.replace(Regex("^[^\\p{L}]+"), ""), Modifier.fontSize(13.px).color(color))
}

@Composable
private fun Badge(text: String, color: com.varabyte.kobweb.compose.ui.graphics.Color) {
    SpanText(
        text,
        Modifier.height(24.px).padding(leftRight = 10.px).borderRadius(999.px)
            .backgroundColor(color.toRgb().copy(alpha = 36)).color(color)
            .fontSize(12.px).fontWeight(FontWeight.SemiBold)
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
    )
}

@Composable
private fun Hint(text: String) {
    val palette = LocalAppTheme.current.colors
    SpanText(
        text,
        Modifier.fillMaxWidth().padding(10.px).borderRadius(10.px).fontSize(13.px)
            .color(palette.statusPending).backgroundColor(palette.statusPending.toRgb().copy(alpha = 24))
    )
}

private fun downloadText(fileName: String, content: String) {
    val blob = js("new Blob([content], { type: 'text/plain;charset=utf-8' })")
    val url = js("URL.createObjectURL(blob)") as String
    val link = document.createElement("a") as HTMLAnchorElement
    link.href = url
    link.download = fileName
    link.click()
    window.setTimeout({ js("URL.revokeObjectURL(url)") }, 1000)
}
